import { useCallback, useEffect, useMemo, useState } from 'react';
import { api, brouillon, environnementServeur, ficheVide, operateur, pourEnvoi, sessionSignature, validerEtape } from './api.js';
import {
  EtapeAffaire, EtapeMateriel, EtapeReserves, EtapeSignataires, EtapeRecap,
} from './steps/Etapes.jsx';
import { Signature } from './steps/Signature.jsx';
import { LeveeReserves } from './steps/LeveeReserves.jsx';
import { SignaturesEnAttente } from './steps/SignaturesEnAttente.jsx';
import { Identification } from './steps/Identification.jsx';

const ETAPES = [
  { titre: 'Affaire', Composant: EtapeAffaire },
  { titre: 'Matériel', Composant: EtapeMateriel },
  { titre: 'Réserves', Composant: EtapeReserves },
  { titre: 'Signataires', Composant: EtapeSignataires },
  { titre: 'Récapitulatif', Composant: EtapeRecap },
];

// Valeur de repli seulement : l'environnement réel est confirmé au démarrage via /sante
// (voir l'état `sandbox` dans App). Le front ne dépend donc plus d'une variable de compilation.
const SANDBOX_REPLI = (import.meta.env.VITE_YOUTRUST_ENV ?? 'sandbox') !== 'production';

export default function App() {
  // Restauration au démarrage : un onglet purgé par le système ne doit pas coûter
  // une ressaisie complète devant le client.
  const restaure = brouillon.charger();
  const [etape, setEtape] = useState(restaure?.etape ?? 0);
  const [fiche, setFiche] = useState(() => ({ ...ficheVide(), ...(restaure?.fiche ?? {}) }));
  const [erreurs, setErreurs] = useState({});
  const [erreursServeur, setErreursServeur] = useState(null);
  const [envoi, setEnvoi] = useState(false);
  const [signature, setSignature] = useState(null); // { ficheId, reference }
  const [fini, setFini] = useState(false);
  // Choix du mode de signature après saisie : { ficheId, reference, nomAffaire, appareilNo }.
  const [choixSignature, setChoixSignature] = useState(null);
  // Écran de confirmation après un envoi à distance : { reference, nomAffaire }.
  const [envoyeADistance, setEnvoyeADistance] = useState(null);
  const [horsLigne, setHorsLigne] = useState(!navigator.onLine);
  const [identifie, setIdentifie] = useState(() => operateur.courant() !== null);

  // Environnement réel confirmé par le serveur au démarrage (source de vérité unique).
  // Repli sur la valeur de compilation le temps de la réponse /sante.
  const [sandbox, setSandbox] = useState(SANDBOX_REPLI);
  useEffect(() => {
    environnementServeur().then((env) => { if (env) setSandbox(env !== 'production'); });
  }, []);

  // Une demande de signature laissée ouverte par un incident précédent.
  const [repriseProposee, setRepriseProposee] = useState(() => sessionSignature.charger());

  // null = écran d'accueil ; 'reception' = saisie d'une fiche ; 'levee' = levée.
  // Deux métiers distincts : le technicien de pose prononce la réception, le
  // conducteur de travaux lève les réserves. Les mélanger dans un seul parcours
  // obligerait chacun à traverser l'écran de l'autre.
  // Un brouillon en cours renvoie directement à la saisie : le technicien reprend là
  // où il s'était arrêté sans repasser par l'accueil.
  const [parcours, setParcours] = useState(restaure?.fiche ? 'reception' : null);

  const set = useCallback((maj) => {
    setFiche((f) => ({ ...f, ...maj }));
    setErreursServeur(null);
  }, []);

  // Persistance à chaque modification. Volontairement sans anti-rebond : le coût
  // d'une écriture localStorage est négligeable face au coût d'une saisie perdue.
  useEffect(() => {
    if (parcours === 'reception' && !signature && !fini) brouillon.enregistrer(fiche, etape);
  }, [parcours, fiche, etape, signature, fini]);

  useEffect(() => {
    const majReseau = () => setHorsLigne(!navigator.onLine);
    window.addEventListener('online', majReseau);
    window.addEventListener('offline', majReseau);
    return () => {
      window.removeEventListener('online', majReseau);
      window.removeEventListener('offline', majReseau);
    };
  }, []);

  const { Composant, titre } = ETAPES[etape];
  const derniere = etape === ETAPES.length - 1;

  const suivant = async () => {
    const e = validerEtape(etape, fiche);
    if (Object.keys(e).length) {
      setErreurs(e);
      // Le premier champ en erreur doit être visible : sur un écran de téléphone,
      // un message d'erreur hors champ de vision équivaut à pas de message.
      requestAnimationFrame(() => {
        document.querySelector('[aria-invalid="true"], .erreur')
          ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      });
      return;
    }
    setErreurs({});

    if (!derniere) {
      setEtape(etape + 1);
      window.scrollTo({ top: 0 });
      return;
    }

    // Dernière étape : enregistrement, puis choix du mode de signature.
    setEnvoi(true);
    setErreursServeur(null);
    try {
      const creee = await api.creerFiche(pourEnvoi(fiche), fiche.appareilNo);
      // On ne lance pas encore : le conducteur choisit « ensemble » ou « à distance ».
      setChoixSignature({
        ficheId: creee.id,
        reference: creee.reference,
        nomAffaire: fiche.nomAffaire,
        appareilNo: fiche.appareilNo,
      });
    } catch (err) {
      setErreursServeur(err.erreurs ?? [{ champ: '', message: err.message }]);
    } finally {
      setEnvoi(false);
    }
  };

  // Lance la signature dans le mode choisi. Présentiel → iframe sur place ; à distance
  // → Youtrust envoie les invitations, on affiche une confirmation.
  const lancerAvecMode = async (mode) => {
    if (!choixSignature) return;
    setEnvoi(true);
    setErreursServeur(null);
    try {
      await api.lancerSignature(choixSignature.ficheId, mode);
      if (mode === 'distance') {
        setEnvoyeADistance({
          reference: choixSignature.reference,
          nomAffaire: choixSignature.nomAffaire,
        });
        setChoixSignature(null);
        brouillon.effacer();
      } else {
        // Trace persistée AVANT d'afficher l'iframe : si le téléphone s'éteint à cet
        // instant précis, la reprise reste possible au redémarrage.
        sessionSignature.enregistrer({
          ficheId: choixSignature.ficheId,
          reference: choixSignature.reference,
          nomAffaire: choixSignature.nomAffaire,
          appareilNo: choixSignature.appareilNo,
        });
        setSignature({ ficheId: choixSignature.ficheId, reference: choixSignature.reference });
        setChoixSignature(null);
      }
    } catch (err) {
      setErreursServeur(err.erreurs ?? [{ champ: '', message: err.message }]);
    } finally {
      setEnvoi(false);
    }
  };

  const precedent = () => {
    setErreurs({});
    setEtape((n) => Math.max(0, n - 1));
    window.scrollTo({ top: 0 });
  };

  // Retour à l'accueil depuis la première étape de saisie. Le brouillon reste
  // enregistré : on ne perd pas ce qui a été saisi, on change simplement d'action.
  const retourAccueil = () => {
    setErreurs({});
    setErreursServeur(null);
    setParcours(null);
    window.scrollTo({ top: 0 });
  };

  // Permet de corriger un matricule mal saisi : on efface l'opérateur et on
  // repasse par l'écran d'identification.
  const changerIdentifiant = () => {
    operateur.oublier();
    setIdentifie(false);
  };

  const jauge = useMemo(
    () => ETAPES.map((_, i) => (
      <span key={i} className={i < etape ? 'faite' : i === etape ? 'active' : ''} />
    )),
    [etape],
  );

  const terminer = useCallback(() => {
    // La fiche est signée : les données personnelles du client n'ont plus à rester
    // dans le stockage du téléphone.
    brouillon.effacer();
    sessionSignature.effacer();
    setFini(true);
  }, []);

  // Depuis l'écran « Fiche signée » : repartir proprement d'une fiche vierge, sans
  // conserver les données du client précédent. Ramène à l'accueil (choix réception /
  // levée). L'identification (matricule) est conservée.
  const recommencer = () => {
    brouillon.effacer();
    sessionSignature.effacer();
    setFiche(ficheVide());
    setEtape(0);
    setErreurs({});
    setErreursServeur(null);
    setSignature(null);
    setFini(false);
    setChoixSignature(null);
    setEnvoyeADistance(null);
    setParcours(null);
    window.scrollTo({ top: 0 });
  };

  // Écran de reprise, proposé avant toute nouvelle saisie.
  if (repriseProposee && !signature && !fini) {
    return (
      <>
        <header className="appli">
          <h1>Fiche de réception travaux</h1>
          <div className="ref">Réf. {repriseProposee.reference}</div>
        </header>
        <main>
          <div className="carte">
            <h2>Signature en cours</h2>
            <p className="sous-titre">
              {repriseProposee.phase === 'levee'
                ? 'Une levée de réserves a été envoyée en signature sans être terminée : '
                : 'Une réception a été envoyée en signature sans être terminée : '}
              <strong>{repriseProposee.nomAffaire}</strong>, appareil {repriseProposee.appareilNo}.
            </p>
            <p className="sous-titre">
              La reprise ne fera pas re-signer les personnes ayant déjà signé, et ne
              modifie pas le document présenté.
            </p>
          </div>
        </main>
        <nav className="barre">
          <button
            type="button"
            onClick={() => { sessionSignature.effacer(); setRepriseProposee(null); }}
          >
            Nouvelle fiche
          </button>
          <button
            type="button"
            className="primaire"
            onClick={() => {
              setSignature({
                ficheId: repriseProposee.ficheId,
                reference: repriseProposee.reference,
                phase: repriseProposee.phase ?? 'reception',
              });
              setRepriseProposee(null);
            }}
          >
            Reprendre
          </button>
        </nav>
      </>
    );
  }

  // Identification obligatoire en tête : le serveur refuse toute requête anonyme.
  if (!identifie) {
    return <Identification onIdentifie={() => setIdentifie(true)} />;
  }

  // Accueil : choix du parcours. Placé après l'écran de reprise, qui est prioritaire —
  // une signature inachevée passe avant toute nouvelle action.
  if (parcours === null && !signature && !fini) {
    return (
      <>
        <header className="appli">
          <h1>Fiche de réception travaux</h1>
          <div className="ref">Etna France{sandbox ? ' · SANDBOX' : ''}</div>
        </header>
        <main>
          <div className="carte">
            <h2>Que souhaitez-vous faire ?</h2>
            <p className="identite-courante">
              Identifié : <strong>{operateur.courant()}</strong>
              <button type="button" className="lien-changer" onClick={changerIdentifiant}>
                Changer
              </button>
            </p>
            <p className="sous-titre">
              La réception se prononce sur chantier, en présence du client. La levée des
              réserves fait l'objet d'une seconde signature, plus tard.
            </p>
            <button type="button" className="bouton-accueil" onClick={() => setParcours('reception')}>
              <span className="bouton-accueil-titre">Prononcer une réception</span>
              <span className="bouton-accueil-detail">
                Nouvelle fiche, signature du client puis du représentant Etna
              </span>
            </button>
            <button type="button" className="bouton-accueil" onClick={() => setParcours('levee')}>
              <span className="bouton-accueil-titre">Lever des réserves</span>
              <span className="bouton-accueil-detail">
                Affaires réceptionnées avec réserves, en attente de levée
              </span>
            </button>
            <button type="button" className="bouton-accueil" onClick={() => setParcours('attente')}>
              <span className="bouton-accueil-titre">Signatures en attente</span>
              <span className="bouton-accueil-detail">
                Suivre et relancer les signatures envoyées à distance
              </span>
            </button>
          </div>
        </main>
      </>
    );
  }

  if (parcours === 'attente') {
    return (
      <>
        <header className="appli">
          <h1>Signatures en attente</h1>
          <div className="ref">Etna France{sandbox ? ' · SANDBOX' : ''}</div>
        </header>
        <main>
          {horsLigne && (
            <div className="bandeau-reseau" role="status">
              <strong>Hors connexion.</strong> Le suivi nécessite du réseau.
            </div>
          )}
          <SignaturesEnAttente onQuitter={() => setParcours(null)} />
        </main>
      </>
    );
  }

  if (parcours === 'levee') {
    return (
      <>
        <header className="appli">
          <h1>Levée des réserves</h1>
          <div className="ref">Etna France{sandbox ? ' · SANDBOX' : ''}</div>
        </header>
        <main>
          {horsLigne && (
            <div className="bandeau-reseau" role="status">
              <strong>Hors connexion.</strong> La liste et la signature nécessitent du réseau.
            </div>
          )}
          <LeveeReserves sandbox={sandbox} onQuitter={() => setParcours(null)} />
        </main>
      </>
    );
  }

  if (choixSignature) {
    return (
      <>
        <header className="appli">
          <h1>Fiche de réception travaux</h1>
          <div className="ref">Réf. {choixSignature.reference}{sandbox ? ' · SANDBOX' : ''}</div>
        </header>
        <main>
          <div className="carte">
            <h2>Comment faire signer ?</h2>
            <p className="sous-titre">
              {choixSignature.nomAffaire} — appareil {choixSignature.appareilNo}.
            </p>
            {erreursServeur && (
              <div className="erreurs-serveur" role="alert">
                {erreursServeur.map((e, i) => <div key={i}>{e.message}</div>)}
              </div>
            )}
            <button
              type="button"
              className="bouton-accueil"
              disabled={envoi}
              onClick={() => lancerAvecMode('presentiel')}
            >
              <span className="bouton-accueil-titre">Signer ensemble, maintenant</span>
              <span className="bouton-accueil-detail">
                Les deux parties sont présentes. Signature sur cet appareil, l'une après l'autre.
              </span>
            </button>
            <button
              type="button"
              className="bouton-accueil"
              disabled={envoi}
              onClick={() => lancerAvecMode('distance')}
            >
              <span className="bouton-accueil-titre">Envoyer à signer à distance</span>
              <span className="bouton-accueil-detail">
                Chaque partie reçoit une invitation par e-mail et signe de son côté, sur son
                téléphone. À suivre dans « Signatures en attente ».
              </span>
            </button>
          </div>
        </main>
      </>
    );
  }

  if (envoyeADistance) {
    return (
      <>
        <header className="appli"><h1>Fiche de réception travaux</h1></header>
        <main>
          <div className="etat succes">
            <div className="titre">Invitations envoyées</div>
            <p className="sous-titre">
              Référence {envoyeADistance.reference}. Chaque partie va recevoir un e-mail
              l'invitant à signer, dans l'ordre : le client d'abord, puis le représentant
              Etna. Vous serez notifié quand tout sera signé. Vous pouvez suivre l'avancement
              dans « Signatures en attente ».
            </p>
          </div>
        </main>
        <nav className="barre">
          <button type="button" className="primaire" onClick={recommencer}>
            Retour à l'accueil
          </button>
        </nav>
      </>
    );
  }

  if (fini) {
    return (
      <>
        <header className="appli"><h1>Fiche de réception travaux</h1></header>
        <main>
          <div className="etat succes">
            <div className="titre">Fiche signée</div>
            <p className="sous-titre">
              Référence {signature.reference}. Le document signé et son dossier de preuves
              sont transmis automatiquement au service travaux. Une copie part par e-mail
              à chaque signataire.
            </p>
            {fiche.avecReserves && (
              <p className="sous-titre">
                Réception prononcée avec réserves : l'affaire ne pourra être soldée
                qu'après signature de la levée.
              </p>
            )}
          </div>
        </main>
        <nav className="barre">
          <button type="button" className="primaire" onClick={recommencer}>
            Retour à l'accueil
          </button>
        </nav>
      </>
    );
  }

  return (
    <>
      <header className="appli">
        <h1>Fiche de réception travaux</h1>
        <div className="ref">
          {signature ? `Réf. ${signature.reference}` : `${titre} — étape ${etape + 1}/${ETAPES.length}`}
          {sandbox && ' · SANDBOX'}
        </div>
      </header>

      {!signature && <div className="jauge">{jauge}</div>}

      <main>
        {horsLigne && (
          <div className="bandeau-reseau" role="status">
            <strong>Hors connexion.</strong> La saisie est conservée sur ce téléphone.
            L'envoi en signature nécessite du réseau : rapprochez-vous d'une zone
            couverte, l'application reprendra d'elle-même.
          </div>
        )}

        {erreursServeur && (
          <div className="bandeau-erreur" role="alert">
            <strong>La fiche n'a pas pu être envoyée en signature.</strong>
            <ul>
              {erreursServeur.map((e, i) => (
                <li key={i}>{e.champ ? `${e.champ} : ` : ''}{e.message}</li>
              ))}
            </ul>
          </div>
        )}

        {signature ? (
          <Signature
            ficheId={signature.ficheId}
            phase={signature.phase ?? 'reception'}
            sandbox={sandbox}
            onTermine={terminer}
            onErreur={(message) => setErreursServeur([{ champ: '', message }])}
          />
        ) : (
          <Composant f={fiche} set={set} err={erreurs} />
        )}
      </main>

      {!signature && (
        <nav className="barre">
          {etape > 0
            ? <button type="button" onClick={precedent}>Retour</button>
            : <button type="button" onClick={retourAccueil}>Accueil</button>}
          <button
            type="button"
            className="primaire"
            onClick={suivant}
            disabled={envoi || (derniere && horsLigne)}
          >
            {envoi ? 'Préparation…' : derniere ? 'Faire signer' : 'Continuer'}
          </button>
        </nav>
      )}
    </>
  );
}
